import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment/moment';
import { ChevronDown } from 'lucide-react';
import formatRupees from '../../utils/currency';

// A plan instalment carries either a fixed due date or an offset in days. The
// offset counts from the plan's start date - the server's, which is the primary
// batch's commencement while that is still ahead - and from today without one.
const formatDueDate = (instalment, planStartDate) => {
  const from = planStartDate ? moment(planStartDate, 'YYYY-MM-DD') : moment();
  const due = instalment?.dueDate
    ? moment(instalment.dueDate)
    : from.add(instalment?.dueFromDay || 0, 'days');
  return due.isValid() ? due.format('DD MMM YYYY') : '—';
};

const planTotal = (plan) => (plan?.instalments || [])
  .reduce((sum, instalment) => sum + Number(instalment?.amount || 0), 0);

const planSummary = (plan) => {
  const count = plan?.instalments?.length || 0;
  return `${count} instalment${count === 1 ? '' : 's'} · ${formatRupees(planTotal(plan))}`;
};

// Selectors rest on a light gray border; the chosen one takes the brand
// border, a 1px ring and a tint, like the plan rows below.
const SELECTOR_IDLE_CLASS = 'border-gray-300 bg-white hover:bg-gray-50';
const SELECTOR_CHOSEN_CLASS = 'border-[#045692] ring-1 ring-[#045692] bg-blue-50';
const modeOptionClass = (isChosen) => `flex-1 flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${isChosen
  ? SELECTOR_CHOSEN_CLASS
  : SELECTOR_IDLE_CLASS}`;
const MODE_RADIO_CLASS = 'w-4 h-4 text-blue-600 border border-gray-300 focus:ring-blue-500';
const MODE_TEXT_CLASS = 'text-[14px] md:text-[18px] font-medium text-gray-900';

function PlanTable({ plan, planStartDate }) {
  const instalments = plan?.instalments || [];
  return (
    <div className="overflow-hidden rounded-lg border border-[#045692]">
      <table className="w-full text-xs md:text-sm">
        <thead>
          <tr className="bg-[#045692] text-white">
            <th scope="col" className="px-3 py-2.5 text-left font-semibold">Instalment No.</th>
            <th scope="col" className="px-3 py-2.5 text-right font-semibold">Amount</th>
            <th scope="col" className="px-3 py-2.5 text-right font-semibold">Due Date</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {instalments.map((instalment, index) => (
            // Plan instalments carry no id of their own; their position is their identity.
            // eslint-disable-next-line react/no-array-index-key
            <tr key={`instalment-${index}`} className="even:bg-gray-50 text-gray-800">
              <td className="px-3 py-2.5">{`Instalment #${index + 1}`}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{formatRupees(instalment?.amount)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums">{formatDueDate(instalment, planStartDate)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-gray-200 bg-blue-50 font-semibold text-gray-900">
            <td className="px-3 py-2.5">Total</td>
            <td className="px-3 py-2.5 text-right tabular-nums">{formatRupees(planTotal(plan))}</td>
            <td className="px-3 py-2.5"><span className="sr-only">No due date for the total</span></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
PlanTable.propTypes = {
  plan: PropTypes.shape({
    instalments: PropTypes.arrayOf(PropTypes.shape({
      amount: PropTypes.number,
      dueDate: PropTypes.string,
      dueFromDay: PropTypes.number,
    })),
  }),
  planStartDate: PropTypes.string,
};
PlanTable.defaultProps = {
  plan: null,
  planStartDate: null,
};

function PaymentOptions({
  instalmentPlans = [], paymentMode, setPaymentMode, selectedPlanId, setSelectedPlanId, isSpecialPrice,
  adminPlan, planStartDate,
}) {
  const [showInstalment, setShowInstalment] = useState(false);

  useEffect(() => {
    setShowInstalment(instalmentPlans?.length > 0 && !isSpecialPrice);
    // Every refetch of the order hands over a fresh list: keep the buyer's pick
    // while it is still offered, and fall back to the first plan otherwise.
    setSelectedPlanId((currentPlanId) => (
      instalmentPlans?.some((plan) => plan.instalmentPlanId === currentPlanId)
        ? currentPlanId
        : instalmentPlans?.[0]?.instalmentPlanId
    ));
  }, [instalmentPlans]);

  const selectedPlan = instalmentPlans.find((plan) => plan.instalmentPlanId === selectedPlanId) || null;

  // A plan the centre set the registration up on is the only way to pay it: no
  // full payment, no other plan (the server refuses them too).
  if (adminPlan) {
    return (
      <div className="mb-6 rounded-lg w-full">
        <h3 className="mb-1 text-[22px] font-bold text-gray-900">Payment Plan</h3>
        <p className="text-[14px] md:text-[16px] text-gray-600 mb-4">
          Your centre has set this registration up on an instalment plan.
        </p>
        <div className="p-4 md:p-5 bg-white border border-[#B8E5FF] rounded-xl shadow-sm">
          <p className="mb-3 text-base md:text-lg font-semibold text-gray-900">{adminPlan.name}</p>
          <PlanTable plan={adminPlan} planStartDate={planStartDate} />
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-lg w-full">
      <h3 className="mb-1 text-[22px] font-bold text-gray-900">Payment Methods</h3>
      <p className="text-[14px] md:text-[16px] text-gray-600 mb-4">Please select a payment method to proceed</p>

      <div className="space-y-4">
        <div className="flex lg:flex-row flex-col gap-y-3 lg:gap-[1%] lg:justify-between w-full">
          <label htmlFor="payment-full" className={modeOptionClass(paymentMode === 'full')}>
            <input
              id="payment-full"
              type="radio"
              value="full"
              name="payment-mode"
              checked={paymentMode === 'full'}
              onChange={(e) => {
                setPaymentMode(e.target.value);
                setSelectedPlanId('');
              }}
              className={MODE_RADIO_CLASS}
            />
            <span className={MODE_TEXT_CLASS}>Full Payment</span>
          </label>

          {showInstalment && (
            <label htmlFor="payment-partial" className={`${modeOptionClass(paymentMode === 'partial')} lg:flex-none lg:w-[49.5%]`}>
              <input
                id="payment-partial"
                type="radio"
                value="partial"
                name="payment-mode"
                checked={paymentMode === 'partial'}
                onChange={(e) => {
                  setPaymentMode(e.target.value);
                  // Choosing Full Payment clears the plan; coming back must not
                  // leave the order billed as an instalment with no plan chosen.
                  if (!selectedPlanId) setSelectedPlanId(instalmentPlans?.[0]?.instalmentPlanId || '');
                }}
                className={MODE_RADIO_CLASS}
              />
              <span className={MODE_TEXT_CLASS}>Instalment Plan</span>
            </label>
          )}
        </div>

        {showInstalment && paymentMode === 'partial' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 p-4 md:p-5 bg-white border border-[#B8E5FF] rounded-xl shadow-sm">
            {/* Mobile: one full-width dropdown. */}
            <label htmlFor="instalment-plan-select" className="block md:hidden">
              <span className="block mb-2 text-base md:text-lg font-semibold text-gray-900">Choose a plan</span>
              <span className="relative block">
                <select
                  id="instalment-plan-select"
                  value={selectedPlanId || ''}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  className="block w-full h-11 pl-3 pr-9 text-sm text-gray-900 bg-white border border-gray-300 rounded-md appearance-none focus:outline-none focus:border-[#045692] focus:ring-2 focus:ring-blue-100"
                >
                  {instalmentPlans.map((plan) => (
                    <option key={plan.instalmentPlanId} value={plan.instalmentPlanId}>
                      {`${plan.name} — ${planSummary(plan)}`}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </label>

            {/* Desktop: a third of the width, one evenly spaced radio row per plan.
                Rows keep a fixed height rather than stretching to the table beside them. */}
            <fieldset className="hidden md:block md:col-span-1 min-w-0">
              <legend className="mb-3 text-lg font-semibold text-gray-900">Choose a plan</legend>
              <div className="flex flex-col gap-2">
                {instalmentPlans.map((plan) => {
                  const isSelected = plan.instalmentPlanId === selectedPlanId;
                  const radioId = `instalment-plan-${plan.instalmentPlanId}`;
                  return (
                    <label
                      key={plan.instalmentPlanId}
                      htmlFor={radioId}
                      className={`flex h-16 shrink-0 items-center gap-3 px-3 border rounded-lg cursor-pointer transition-colors ${isSelected
                        ? SELECTOR_CHOSEN_CLASS
                        : SELECTOR_IDLE_CLASS}`}
                    >
                      <input
                        id={radioId}
                        type="radio"
                        name="instalmentPlanId"
                        value={plan.instalmentPlanId}
                        checked={isSelected}
                        onChange={() => setSelectedPlanId(plan.instalmentPlanId)}
                        className="w-4 h-4 shrink-0 text-blue-600 border border-gray-300 focus:ring-blue-500"
                      />
                      <span className="min-w-0">
                        {/* truncate: a long plan name must not grow the fixed-height row. */}
                        <span className={`block truncate text-sm font-semibold ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                          {plan.name}
                        </span>
                        <span className="block truncate text-xs text-gray-500">{planSummary(plan)}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            {/* Two thirds on desktop, full width stacked below the dropdown on mobile. */}
            <div className="md:col-span-2 min-w-0">
              <PlanTable plan={selectedPlan} planStartDate={planStartDate} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

PaymentOptions.propTypes = {
  instalmentPlans: PropTypes.arrayOf(
    PropTypes.shape({
      instalmentPlanId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      isActive: PropTypes.bool,
      instalments: PropTypes.arrayOf(
        PropTypes.shape({
          amount: PropTypes.number,
          dueDate: PropTypes.string,
          dueFromDay: PropTypes.number,
        }),
      ).isRequired,
    }),
  ),
  setSelectedPlanId: PropTypes.func.isRequired,
  paymentMode: PropTypes.string.isRequired,
  setPaymentMode: PropTypes.func.isRequired,
  selectedPlanId: PropTypes.string,
  isSpecialPrice: PropTypes.bool,
  adminPlan: PropTypes.shape({
    name: PropTypes.string,
    instalments: PropTypes.arrayOf(PropTypes.shape({
      amount: PropTypes.number,
      dueDate: PropTypes.string,
      dueFromDay: PropTypes.number,
    })),
  }),
  planStartDate: PropTypes.string,
};
PaymentOptions.defaultProps = {
  instalmentPlans: [],
  selectedPlanId: '',
  isSpecialPrice: false,
  adminPlan: null,
  planStartDate: null,
};

export default PaymentOptions;
